<template>
    <section class="tag-bg-section">
        <div class="tag-bg-section__container">
            <CardTitle v-if="!hideTitle" :module="item" />
            <div class="tag-bg-section__grid" :class="colsClass">
                <article v-for="(cell, idx) in fieldData" :key="String((cell as Record<string, unknown>)?.id ?? idx)"
                    class="tag-card" @click="handleCellClick(cell)">
                    <!-- 背景图 -->
                    <div v-if="getCellBg(cell)" class="tag-card__bg">
                        <img :src="getCellBg(cell)" :alt="getCellTitle(cell)" loading="lazy" />
                    </div>
                    <div class="tag-card__veil"></div>

                    <!-- 内容 -->
                    <div class="tag-card__content">
                        <div class="tag-card__top">
                            <span v-if="getCellTag(cell)" class="tag-card__tag">{{ getCellTag(cell) }}</span>
                            <img v-if="getCellIcon(cell)" :src="getCellIcon(cell)" :alt="getCellTitle(cell)"
                                loading="lazy" class="tag-card__icon" />
                        </div>
                        <h4 v-if="getCellTitle(cell)" class="tag-card__title">{{ getCellTitle(cell) }}</h4>
                    </div>
                </article>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Module } from '@/api/page_config'
import { getStrWithRecord, jumpToUrl } from '.';

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

const fieldData = props.item.fieldData

const cols = computed<number>(() => {
    const c = fieldData.length
    if (Number.isFinite(c) && c >= 2 && c <= 4) return c
    return 3
})
const colsClass = computed(() => `is-cols-${cols.value}`)

function getCellBg(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}
function getCellTag(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}
function getCellIcon(cell: unknown): string {
    return getStrWithRecord(cell, 'icon')
}
function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'content')
}

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    jumpToUrl(getStrWithRecord(cell, 'url'))
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$card-radius: 12px;

.tag-bg-section {
    width: 100%;
}

.tag-bg-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.tag-bg-section__grid {
    display: grid;
    gap: 0.875rem;
    grid-template-columns: repeat(5, 1fr);

    .tag-card {
        &:nth-child(4n+1) {
            grid-column: span 3;
        }

        &:nth-child(4n+2) {
            grid-column: span 2;
        }

        &:nth-child(4n+3) {
            grid-column: span 2;
        }

        &:nth-child(4n) {
            grid-column: span 3;
        }
    }

    @media (min-width: $breakpoint-sm) {
        gap: 1rem;
    }

    @media (min-width: $breakpoint-md) {
        gap: 1.25rem;
        grid-template-columns: repeat(7, 1fr);

        .tag-card {
            &:nth-child(6n+1) {
                grid-column: span 3;
            }

            &:nth-child(6n+2) {
                grid-column: span 2;
            }

            &:nth-child(6n+3) {
                grid-column: span 2;
            }

            &:nth-child(6n+4) {
                grid-column: span 2;
            }

            &:nth-child(6n+5) {
                grid-column: span 2;
            }

            &:nth-child(6n) {
                grid-column: span 3;
            }
        }
    }
}

.tag-card {
    position: relative;
    border-radius: $card-radius;
    overflow: hidden;
    cursor: pointer;
    min-height: 70px;
    padding: 1rem 1.25rem;
    background: #fff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    @media (min-width: $breakpoint-md) {
        min-height: 110px;
        padding: 1.25rem 1.5rem;
    }

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }

    &__bg {
        position: absolute;
        inset: 0;
        z-index: 0;

        img {
            width: 100%;
            height: 100%;
            object-fit: fill;
        }
    }

    &__veil {
        position: absolute;
        inset: 0;
        z-index: 1;
        background: linear-gradient(130deg, #ffffff 52%, #ffffff80 92%);
    }

    &__content {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
    }

    &__top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
    }

    &__tag {
        font-size: 0.75rem;
        color: $color-text-secondary;
        font-weight: 500;
        line-height: 1.4;

        @media (min-width: $breakpoint-md) {
            font-size: 0.8125rem;
        }
    }

    &__icon {
        width: 24px;
        height: 24px;
        color: $color-text;
        flex-shrink: 0;
        opacity: 0.7;

        :deep(svg) {
            width: 100%;
            height: 100%;
            stroke-width: 1.5;
        }
    }

    &__title {
        font-size: 1rem;
        font-weight: 800;
        color: $color-text;
        margin: .25rem 0 0;
        line-height: 1.4;
        letter-spacing: -0.01em;

        @media (min-width: $breakpoint-md) {
            font-size: 1.125rem;
            margin-top: .5rem;
        }
    }
}
</style>
